import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as menuCategoryApi from 'api/menuCategoryApi';
import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import {
  detailActions,
  detailSelectors,
} from 'store/reducers/menuCategoryReducer/detailSlice';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { useListStore } from './useListStore';
import {
  mapFormFieldsToMenuCategoryEditPayload,
  mapMenuCategoryToFormFields,
  MenuCategoryEditFormFields,
} from '../types/menuCategoryEditFormFields';

/**
 * メニューカテゴリー詳細 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useDetailStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const selectedMenuCategory = useAppSelector(
    detailSelectors.selectedMenuCategory,
  );
  const { fetchMenuCategories, getMenus } = useListStore();
  const currentModalStep = useAppSelector(detailSelectors.currentModalStep);
  const [canDelete, setCanDelete] = useState(false);

  const form = useForm<MenuCategoryEditFormFields>({
    defaultValues: mapMenuCategoryToFormFields(selectedMenuCategory || {}),
    mode: 'all',
  });
  const { getValues, reset, handleSubmit } = form;

  useEffect(() => {
    if (selectedMenuCategory) {
      reset(mapMenuCategoryToFormFields(selectedMenuCategory));
    }
  }, [selectedMenuCategory, reset]);

  useEffect(() => {
    if (selectedMenuCategory) {
      const menus = getMenus(selectedMenuCategory.id);
      setCanDelete(menus.length === 0);
    }
  }, [selectedMenuCategory, getMenus]);

  const onCloseModal = useCallback(() => {
    void fetchMenuCategories();
    dispatch(detailActions.initialize());
    setCanDelete(false);
  }, [dispatch, fetchMenuCategories]);

  const onOpenModalDelete = useCallback(() => {
    dispatch(detailActions.updateIsOpenModalDelete(true));
  }, [dispatch]);

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchMenuCategories();
  };

  const onConfirm = () => {
    dispatch(detailActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(detailActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: MenuCategoryEditFormFields) => {
      if (selectedMenuCategory) {
        dispatch(loaderActions.setLoading(true));
        const result = await menuCategoryApi.editMenuCategory(
          selectedMenuCategory.id,
          mapFormFieldsToMenuCategoryEditPayload(values),
        );
        if (result.ok) {
          dispatch(detailActions.updateStep(REGISTRATION_STEP.RESULT));
        } else {
          toast({
            status: 'error',
            description: 'カテゴリーの更新に失敗しました。',
          });
        }
        dispatch(loaderActions.setLoading(false));
      }
    },
    () => {
      toast({
        status: 'error',
        description: 'カテゴリーの更新に失敗しました。',
      });
    },
  );

  return {
    /** 選択中のメニューカテゴリー */
    selectedMenuCategory,
    /** メニューカテゴリー編集モーダルのステップ */
    currentModalStep,
    /** メニューカテゴリー詳細モーダルの表示状態 */
    isOpenModal: !!selectedMenuCategory,
    /** メニューカテゴリー詳細を閉じる */
    onCloseModal,
    /** 編集完了後モーダル閉じる */
    onCloseModalWhenCompleted,
    /** 確認画面へ */
    onConfirm,
    /** 入力画面へ戻る */
    onGoBack,
    /** 入力を送信 */
    onSubmit,
    /** フォーム */
    form,
    /** フォーム values */
    values: getValues(),
    /** メニューカテゴリーの削除確認モーダルを表示 */
    onOpenModalDelete,
    /** 削除可能フラグ */
    canDelete,
  };
};
